/* screens/workspace/canvas/MenuCanvas.jsx — inline-канвас проекта меню (S6).
   view: концепция, метрики (позиций/средний чек/цель FC%/медианный FC%), таблица позиций
   по разделам с цветокодом FC% (<25/25–30/≥30), чипы стоп-лист/черновик.
   edit: правка концепции, среднего чека, целевого FC% и цены позиций; удаление позиции —
   через общий ConfirmDialog (фикс №13, не window.confirm). Save — локальная (только превью):
   канвас read-only, постоянное сохранение меню живёт в редакторе Кухни (saveMenuFull).
   Источник: 07 §2.10. ХЕНДОФФ → frontend/components/chat/MenuCanvas.jsx. */

import React from 'react';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { Pill } from '../../../components/ui/Badge.jsx';
import { NumberInput, Textarea } from '../../../components/ui/Field.jsx';
import { Icon } from '../../../components/ui/Icon.jsx';
import { fmt } from '../../../lib/format.js';
import { RR } from '../../../lib/router.jsx';
import { menuFcTone } from '../../../lib/uiHelpers.js';
import { CanvasEditForm } from './CanvasEditForm.jsx';
import { CanvasErrorState, CanvasMetrics, CanvasSection, CanvasSkeleton, CanvasViewForm, useCanvasLoad } from './CanvasViewForm.jsx';

  const { useState } = React;
  const { useNavigate } = RR;

  function median(arr) {
    const v = arr.filter((x) => x != null).sort((a, b) => a - b);
    if (!v.length) return null;
    const m = Math.floor(v.length / 2);
    return v.length % 2 ? v[m] : Math.round((v[m - 1] + v[m]) / 2);
  }

  // Единая точка расчёта FC% (derived): «код считает». Та же точность (1 знак), что и
  // серверный/recompute-путь черновиков — чтобы источник↔derived не дрейфовали (P1-17).
  function computeFc(cost, price) {
    if (!cost || !price) return null;
    return Math.round((cost / price) * 1000) / 10;
  }

  function metricsOf(d) {
    const fcs = (d.dishes || []).map((x) => x.food_cost_pct).filter((x) => x != null);
    return [
      { label: 'Позиций', value: (d.dishes || []).length },
      { label: 'Средний чек', value: fmt.money(d.average_check) },
      { label: 'Цель FC', value: d.target_food_cost_pct != null ? fmt.percent(d.target_food_cost_pct, { digits: 1 }) : '—' },
      { label: 'Медианный FC', value: fcs.length ? fmt.percent(median(fcs), { digits: 1 }) : '—' },
    ];
  }

  function PositionsTable({ d, edit, onPrice, onRemove }) {
    const navigate = useNavigate();
    return (
      <div className="cv-table-wrap">
        {(d.sections || []).map((sec) => {
          const list = (d.dishes || []).filter((x) => x.section_id === sec.id);
          if (!list.length) return null;
          return (
            <div className="cv-table-group" key={sec.id}>
              <div className="cv-table-group__title t-eyebrow">{sec.title} <span className="num text-mute">({list.length})</span></div>
              <table className="cv-table">
                <thead><tr>
                  <th>Позиция</th><th className="num">Выход</th><th className="num">Себест.</th><th className="num">Цена</th><th className="num">FC%</th><th />
                </tr></thead>
                <tbody>
                  {list.map((p) => (
                    <tr key={p.id}>
                      <td data-label="Позиция">
                        <button type="button" className="cv-linkname" onClick={() => p.ttk_id && navigate(`/kitchen?tab=recipes&edit=ttk-${p.ttk_id}`)} disabled={!p.ttk_id}>
                          <span className="truncate">{p.title}</span>
                        </button>
                        <span className="cv-rowchips">
                          {p.is_stub && <Pill tone="neutral" size="sm" glyph={false}>черновик</Pill>}
                          {p.in_stop_list && <Pill tone="bad" size="sm" glyph={false}>стоп-лист</Pill>}
                        </span>
                      </td>
                      <td className="num" data-label="Выход">{p.output_g != null ? fmt.grams(p.output_g) : '—'}</td>
                      <td className="num derived-cell" data-label="Себест.">{p.cost_per_portion_rub != null ? fmt.money(p.cost_per_portion_rub) : '—'}</td>
                      <td className="num" data-label="Цена">
                        {edit
                          ? <NumberInput plain value={p.selling_price_rub ?? ''} unit="₽" step={10} min={0} onChange={(v) => onPrice(p.id, v === '' ? null : Number(v))} />
                          : (p.selling_price_rub != null ? fmt.money(p.selling_price_rub) : '—')}
                      </td>
                      <td className="num derived-cell" data-label="FC%"><span className={'cv-fc fc-tone-' + menuFcTone(p.food_cost_pct, d.target_food_cost_pct)}>{p.food_cost_pct != null ? fmt.percent(p.food_cost_pct, { digits: 1 }) : '—'}</span></td>
                      <td className="cv-table__act">
                        {edit && <button type="button" className="cv-iconbtn" aria-label="Удалить позицию" onClick={() => onRemove(p)}><Icon name="trash" size={15} /></button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  }

  function MenuCanvas({ spec, onClose, panel }) {
    const { kind, data, partial, error, retry } = useCanvasLoad(spec);
    const navigate = useNavigate();
    const toast = useToast();
    const confirm = useConfirm();
    const [mode, setMode] = useState('view');
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);

    const id = data ? data.id : spec.id;
    const openHref = `/kitchen?tab=menus&edit=menu-${id}`;
    const header = { eyebrow: 'Проект меню', icon: 'reports', title: (data && data.name) || (partial && partial.title) || 'Проект меню', panel };

    if (kind === 'loading') return <CanvasViewForm {...header} onClose={onClose}><CanvasSkeleton note="Собираю проект меню…" /></CanvasViewForm>;
    if (kind === 'error') return <CanvasViewForm {...header} onClose={onClose}><CanvasErrorState error={error} message="Не удалось загрузить меню" notFoundMessage="Проект меню не найден или был удалён." onRetry={retry} /></CanvasViewForm>;
    if (kind === 'partial') {
      return (
        <CanvasViewForm {...header} onClose={onClose}>
          <div className="cv-partial"><div className="cv-partial__badge"><Icon name="spinner" size={13} /> Собираю разделы…</div>
            <ul className="cv-partial__list">{(partial.sections_preview || []).map((s, i) => <li className="cv-partial__item" key={i}><span className="cv-partial__dot" />{s}</li>)}</ul>
          </div>
        </CanvasViewForm>
      );
    }

    const view = mode === 'view' ? data : draft;

    const enterEdit = () => { setDraft(JSON.parse(JSON.stringify(data))); setMode('edit'); };
    const cancel = () => { setDraft(null); setMode('view'); };
    // chat-2: канвас — read-only превью меню; полноценное сохранение живёт в редакторе Кухни
    // (saveMenuFull, per-op diff). Локальная правка в канвасе НЕ персистится на бэк — отражаем
    // её только в превью и предлагаем «Открыть в редакторе» для реального сохранения.
    const save = () => {
      Object.assign(data, draft);
      setMode('view'); setDraft(null);
      toast.show('Изменения учтены в превью. Сохраните их в редакторе меню.', 'muted');
    };
    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    // Цена — поле-источник; FC% — derived: считаем единым computeFc (та же точность, что и
    // recompute-сервис черновиков), а не локальной ad-hoc формулой (P1-17).
    const setPrice = (pid, v) => setDraft((d) => {
      const dishes = d.dishes.map((x) => {
        if (x.id !== pid) return x;
        return { ...x, selling_price_rub: v, food_cost_pct: computeFc(x.cost_per_portion_rub, v) };
      });
      return { ...d, dishes };
    });
    const removePos = async (p) => {
      const ok = await confirm({ title: 'Удалить позицию?', body: `Блюдо «${p.title}» будет убрано из проекта меню.`, confirmLabel: 'Удалить', cancelLabel: 'Отмена', tone: 'danger' });
      if (!ok) return;
      setDraft((d) => ({ ...d, dishes: d.dishes.filter((x) => x.id !== p.id) }));
    };

    const body = (
      <>
        {mode === 'view' && view.concept && <CanvasSection title="Концепция"><p className="cv-text">{view.concept}</p></CanvasSection>}
        {mode === 'edit' && (
          <CanvasSection title="Концепция и экономика">
            <Textarea value={view.concept || ''} onChange={(e) => setField('concept', e.target.value)} rows={2} placeholder="Концепция меню…" />
            <div className="cv-edit-row">
              <label className="cv-edit-field"><span className="field__label">Средний чек</span>
                <NumberInput value={view.average_check ?? ''} unit="₽" step={50} min={0} onChange={(v) => setField('average_check', v === '' ? null : Number(v))} /></label>
              <label className="cv-edit-field"><span className="field__label">Целевой FC%</span>
                <NumberInput value={view.target_food_cost_pct ?? ''} unit="%" step={1} min={0} onChange={(v) => setField('target_food_cost_pct', v === '' ? null : Number(v))} /></label>
            </div>
          </CanvasSection>
        )}
        <CanvasMetrics items={metricsOf(view)} />
        <CanvasSection title="Позиции">
          <PositionsTable d={view} edit={mode === 'edit'} onPrice={setPrice} onRemove={removePos} />
        </CanvasSection>
      </>
    );

    if (mode === 'edit') {
      return <CanvasEditForm {...header} onClose={onClose} onCancel={cancel} onSave={save} busy={busy}>{body}</CanvasEditForm>;
    }
    const footer = (
      <div className="cv-foot-actions">
        <button type="button" className="btn btn--secondary btn--sm" onClick={enterEdit}><Icon name="edit" size={14} /><span className="btn__label">Изменить</span></button>
        <button type="button" className="btn btn--primary btn--sm" onClick={() => navigate(openHref)}><span className="btn__label">Открыть в редакторе</span><Icon name="arrow-up-right" size={14} /></button>
      </div>
    );
    return <CanvasViewForm {...header} onClose={onClose} openHref={openHref} openLabel="Открыть" footer={footer}>{body}</CanvasViewForm>;
  }

export { MenuCanvas };
